/**
 * Component Registry for Z Modular Theme
 *
 * Extends the base aster theme registry with Z Modular specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-aster/registry';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { Footer } from './components/Footer';
import { FloorplansList } from './components/FloorplansList';
import { ConvenienceCallout } from './components/ConvenienceCallout';
import { PropertyOverview } from './components/PropertyOverview';
import { Testimonials } from './components/Testimonials';
import { LetsConnect } from './components/LetsConnect';
import { PhotoSlider } from './components/PhotoSlider';
import { HeroSolidBG } from './components/HeroSolidBG';
import { AmenitiesHero } from './components/AmenitiesHero';
import { FloorPlansHero } from './components/FloorPlansHero';
import { GalleryHero } from './components/GalleryHero';
import { GalleryApartments } from './components/GalleryApartments';
import { NeighborhoodHero } from './components/NeighborhoodHero';
import { ContactHero } from './components/ContactHero';
import { VirtualTourHero } from './components/VirtualTourHero';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { ContactForm } from './components/ContactForm';
import { ScheduleTourForm } from './components/ScheduleTourForm';
import { ResidentAccess } from './components/ResidentAccess';
import { WhiteStripe } from './components/WhiteStripe';
import { FAQ } from './components/FAQ';
import { SitemapContent } from './components/SitemapContent';
import { AmenitiesList } from './components/AmenitiesList';
import { LifestyleFeatureRows } from './components/LifestyleFeatureRows';
import { LifestyleResidentTypes } from './components/LifestyleResidentTypes';
import { LifestyleClosingCTA } from './components/LifestyleClosingCTA';
import { FlexibleLivingPrograms } from './components/FlexibleLivingPrograms';
import { RewardsPrograms } from './components/RewardsPrograms';
import { BetterBuiltIntro } from './components/BetterBuiltIntro';
import { BetterBuiltConstruction } from './components/BetterBuiltConstruction';
import { BetterBuiltClosing } from './components/BetterBuiltClosing';
import { PetPolicyInfo } from './components/PetPolicyInfo';
import { Reviews } from './components/Reviews';
import { MeetTheTeamIntro } from './components/MeetTheTeamIntro';
import { MeetTheTeamMembers } from './components/MeetTheTeamMembers';
import { MeetTheTeamContactCTA } from './components/MeetTheTeamContactCTA';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { HyperlocalHero } from './components/HyperlocalHero';
import { InteractiveMapSection } from './components/InteractiveMapSection';

/**
 * Z Modular specific component overrides.
 * These components have been customized for the Z Modular brand.
 */
const zmodularOverrides: Partial<ComponentRegistry> = {
  Header,
  Hero,
  Footer,
  FloorplansList,
  ConvenienceCallout,
  PropertyOverview,
  Testimonials,
  LetsConnect,
  PhotoSlider,
  HeroSolidBG,
  AmenitiesHero,
  FloorPlansHero,
  GalleryHero,
  GalleryApartments,
  NeighborhoodHero,
  ContactHero,
  VirtualTourHero,
  VirtualTourEmbed,
  NeighborhoodMap,
  ContactForm,
  ScheduleTourForm,
  ResidentAccess,
  WhiteStripe,
  FAQ,
  SitemapContent,
  AmenitiesList,
  LifestyleFeatureRows,
  LifestyleResidentTypes,
  LifestyleClosingCTA,
  FlexibleLivingPrograms,
  RewardsPrograms,
  BetterBuiltIntro,
  BetterBuiltConstruction,
  BetterBuiltClosing,
  PetPolicyInfo,
  Reviews,
  MeetTheTeamIntro,
  MeetTheTeamMembers,
  MeetTheTeamContactCTA,
  SpecialOfferBanner,
  HyperlocalHero,
  InteractiveMapSection,
};

/**
 * Final merged registry for Z Modular properties.
 * Inherits all components from the aster theme, with Z Modular overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...zmodularOverrides,
};

export default registry;
